import './CostBasisTable.scss';
import './UnrealizedCostBasisTableV2.scss';

import { array } from 'prop-types';
import React, { useState, useEffect } from 'react';
import LocalBeaconTable from 'components/shared/Table/BeaconTable';
import {
  costBasisRealizedData,
  generateCategoryAndAccountTypeRows,
  getRealizedRows,
  groupAgreementsWithChildrenRows,
  sortAgreementsByName
} from './TaxesPageUtility';
import { realizedTableHeaders } from 'components/shared/Text/ColumnHeaders';
import { BROKERAGE, getProductName } from 'services/Products';
import CostBasisErrorV2 from './CostBasisErrorV2';
import {connect} from "react-redux";
import { getIsPrintMode } from 'state/taxes/TaxSelectors';

const RealizedCostBasisTableV2 = ({
  costBasisBalances,
  realizedPershingData,
  isPrintMode,
  taxonomyDataV2,
  hasURZOrRZFail,
  pershingAccounts,
  preScrollFormatMobile,
  type
}) => {
  const [openedRows, setOpenedRows] = useState({});
  const [prePrintOpenRows, setPrePrintOpenRows] = useState({});
  const [collapsedRows, setCollapsedRows] = useState({});
  const [mfTableData, setMFTableData] = useState([]);
  useEffect(() => {
    const flattenedCostBasisData = costBasisRealizedData(costBasisBalances);
    setMFTableData(flattenedCostBasisData);
  }, [costBasisBalances]);

  useEffect(() => {
    if (isPrintMode) {
      const mutualFundDataOpened = mfTableData.reduce((acc, item) => {
        acc[item.taxesUniqueId] = true;
        return acc;
      }, {});

      const brokerageDataOpened = realizedPershingData?.reduce((acc, item) => {
        acc[item.taxesUniqueId] = true;

        if (item.holdings) {
          item.holdings.forEach((holding) => {
            acc[holding.taxesUniqueId] = true;
          });
        }

        return acc;
      }, {});

      const allRowsOpened = { ...mutualFundDataOpened, ...brokerageDataOpened };
      setPrePrintOpenRows(openedRows);
      setOpenedRows(allRowsOpened);
    } else {
      setOpenedRows(prePrintOpenRows);
    }
  }, [isPrintMode, mfTableData]);

  useEffect(() => {
    const updatedCollapsedRows = { ...collapsedRows };

    // for every account that is closed, add the holdings to be collapsed
    realizedPershingData?.forEach((item) => {
      if (openedRows[item.taxesUniqueId] === false) {
        Object.entries(openedRows).forEach(([key]) => {
          if(key.includes(item.taxesUniqueId)) {
            updatedCollapsedRows[key] = true;
          }
        });
      }
    });

    if (
      JSON.stringify(updatedCollapsedRows) !== JSON.stringify(collapsedRows)
    ) {
      setCollapsedRows(updatedCollapsedRows);
    }
  }, [openedRows, realizedPershingData]);

  useEffect(() => {
    const updatedOpenedRows = { ...openedRows };
    const updatedCollapsedRows = { ...collapsedRows };

    // for every account that is collapsed
    Object.entries(collapsedRows).forEach(([key]) => {
      delete updatedOpenedRows[key];
      delete updatedCollapsedRows[key];
    });

    if (JSON.stringify(updatedOpenedRows) !== JSON.stringify(openedRows)) {
      setOpenedRows(updatedOpenedRows);
    }

    if (
      JSON.stringify(updatedCollapsedRows) !== JSON.stringify(collapsedRows)
    ) {
      setCollapsedRows(updatedCollapsedRows);
    }
  }, [collapsedRows, realizedPershingData]);

  function getPaginationIndex(agreementId, rowsArray) {
    for (const rowIndex in rowsArray) {
      const row = rowsArray[rowIndex];
      if (row.agreementID === agreementId) {
        return rowIndex;
      }
    }
    return '';
  }

  // get the page # from the index in array
  function getPageNumFromIndex(arrIndex, rowsPerPage) {
    return parseInt(arrIndex / rowsPerPage) + 1;
  }

  return taxonomyDataV2.length > 0 &&
    (mfTableData.length > 0 || realizedPershingData?.length > 0) ? (
    taxonomyDataV2
      .map((hierarchy, i) => {
        let tableRows = [];
        tableRows = [
          ...tableRows,
          {
            ...generateCategoryAndAccountTypeRows(
              'category-row remove-border-block-end',
              hierarchy.name,
              realizedTableHeaders
            )
          }
        ];

        const filteredAccountGroup = hierarchy.accountGroup.filter(
          (accountType) => {
            return accountType.agreements.some((agreement) => {
              return (
                mfTableData.some(
                  (fund) =>
                    fund.taxesUniqueId === agreement.agreementKey.agreementID
                ) ||
                realizedPershingData?.some(
                  (fund) =>
                    fund.taxesUniqueId === agreement.agreementKey.agreementID
                )
              );
            });
          }
        );

        if (filteredAccountGroup.length === 0) {
          return null;
        }

        filteredAccountGroup.map((accountType) => {
          tableRows = [
            ...tableRows,
            {
              ...generateCategoryAndAccountTypeRows(
                'account-type-row blocked_data',
                accountType.name,
                realizedTableHeaders
              )
            }
          ];

          let agreementsInAccountType = [];
          const hasBrokerage = accountType.agreements.some(
            (agreement) =>
              agreement.agreementKey.productType.enumerationID === BROKERAGE
          );

          const filteredAgreement = [];
          accountType.agreements.map((agreement) => {
            const costBasisFund = mfTableData.find(
              (fund) => fund.taxesUniqueId === agreement.agreementKey.agreementID
            );
            const pershingData = realizedPershingData?.find(
              (fund) => fund.taxesUniqueId === agreement.agreementKey.agreementID
            );
            if (costBasisFund && !pershingData) {
              const realizedRows = getRealizedRows(
                costBasisFund.taxesUniqueId,
                costBasisFund,
                openedRows,
                setOpenedRows,
                agreement,
                false
              );

              if (hasBrokerage) {
                agreementsInAccountType = [
                  ...agreementsInAccountType,
                  ...realizedRows
                ];
              } else {
                tableRows = [...tableRows, ...realizedRows];
              }
            }
            const {
              agreementKey: {
                productType: { enumerationID }
              }
            } = agreement;
            if (
              !filteredAgreement.find(
                (account) =>
                  account.agreementKey.productType.enumerationID ===
                  enumerationID
              ) ||
              pershingData.recordStatus !== 'UNKNOWN' ||
              hasURZOrRZFail
            ) {
              if (pershingData) {
                if (pershingData.recordStatus === 'UNKNOWN' || hasURZOrRZFail) {
                  filteredAgreement.push(agreement);
                }
                const realizedRows = getRealizedRows(
                  pershingData.taxesUniqueId,
                  pershingData,
                  openedRows,
                  setOpenedRows,
                  agreement,
                  true,
                  hasURZOrRZFail
                );

                if (hasBrokerage) {
                  agreementsInAccountType = [
                    ...agreementsInAccountType,
                    ...realizedRows
                  ];
                } else {
                  tableRows = [...tableRows, ...realizedRows];
                }
              }
            }
          });

          if (hasBrokerage) {
            // group the agreements by accountID field
            const groupedAgreementsByID = groupAgreementsWithChildrenRows(
              agreementsInAccountType
            );
            // sort the grouped agreements by the parent accountName
            agreementsInAccountType = sortAgreementsByName(
              groupedAgreementsByID
            );
            // then add sorted agreements to the tableRows array
            tableRows = [...tableRows, ...agreementsInAccountType];
          }
        });

        /* if an anchor url is given, identify the row to scroll to */
        let defaultCurrentPage = 1;
        const rowsPerPage = 15;
        let defaultStartPage = 0;
        let defaultEndPage =
          rowsPerPage < tableRows.length ? rowsPerPage : tableRows.length;
        let entryIndex = -1; // saving the index of the row for prescrolling

        if (
          preScrollFormatMobile &&
          type === preScrollFormatMobile.tableFormat
        ) {
          const ind = getPaginationIndex(
            preScrollFormatMobile.taxesUniqueId,
            tableRows
          );
          if (ind !== '') {
            entryIndex = ind;
            defaultCurrentPage = getPageNumFromIndex(ind, rowsPerPage);
            defaultStartPage = (defaultCurrentPage - 1) * rowsPerPage;
            defaultEndPage =
              defaultCurrentPage * rowsPerPage < tableRows.length
                ? defaultCurrentPage * rowsPerPage
                : tableRows.length;
          }
        }

        return (
          <div className="cost-basis-table-container-v2" key={i}>
            <div
              className="cds-table-v2"
              data-prescroll={
                /* for identifying the table as the one to load with prescroll */
                preScrollFormatMobile &&
                entryIndex > -1 &&
                preScrollFormatMobile.tableFormat === type
                  ? true
                  : null
              }>
              <LocalBeaconTable
                key={i}
                idName={'cost-basis-taxonomy-beacon-table-v2'}
                headers={realizedTableHeaders}
                rowsArray={tableRows}
                variant={'fixed'}
                renderStickyRows={true}
                rowsPerPage={rowsPerPage}
                defaultCurrentPage={defaultCurrentPage}
                defaultStartPage={defaultStartPage}
                defaultEndPage={defaultEndPage}
                type={type}
              />
            </div>
          </div>
        );
      })
      .filter((hierarchy) => hierarchy)
  ) : hasURZOrRZFail && pershingAccounts ? (
    pershingAccounts.map((account) => (
      <div
        className="cost-basis-section-error-v2"
        key={account.accountDisplayName}>
        <CostBasisErrorV2 name={getProductName(account.enumerationID)} />
      </div>
    ))
  ) : (
    <span className="cost-basis-no-data-available b-typography-sans-serif--headline--50">
      No data available for the criteria selected.
    </span>
  );
};

RealizedCostBasisTableV2.propTypes = {
  costBasisBalances: array.isRequired
};

const mapStateToProps = (state) => ({
  isPrintMode: getIsPrintMode(state)
});

export default connect(mapStateToProps)(RealizedCostBasisTableV2);
